/**
 * Plain sentences for rules, so the owner can check each one against the
 * wording of the plan. "Mother has Child every other weekend, Friday 6:00 PM
 * to Sunday 5:00 PM, starting Fri Oct 10, 2025."
 */
import type { FixedPoint, PartyRef, RuleBody } from '@kept/schedule';

export interface Names {
  parties: { id: string; label: string }[];
  child: string;
}

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const HOLIDAYS: Record<string, string> = {
  newYearsDay: 'New Year’s Day',
  mlkDay: 'Martin Luther King Jr. Day',
  presidentsDay: 'Presidents’ Day',
  easter: 'Easter',
  memorialDay: 'Memorial Day',
  juneteenth: 'Juneteenth',
  independenceDay: 'Independence Day',
  laborDay: 'Labor Day',
  columbusDay: 'Columbus Day',
  veteransDay: 'Veterans Day',
  thanksgiving: 'Thanksgiving',
  christmasEve: 'Christmas Eve',
  christmasDay: 'Christmas Day',
  newYearsEve: 'New Year’s Eve',
};

const BREAKS: Record<string, string> = {
  spring: 'Spring break',
  thanksgiving: 'Thanksgiving break',
  winter: 'Winter break',
  fall: 'Fall break',
};

export function holidayName(name: string): string {
  return HOLIDAYS[name] ?? name;
}

export function breakName(kind: string): string {
  return BREAKS[kind] ?? `${kind.charAt(0).toUpperCase()}${kind.slice(1)} break`;
}

/** "6:00 PM", "12:00 AM". */
export function clock(time: string): string {
  const [h, m] = time.split(':').map(Number) as [number, number];
  const suffix = h < 12 ? 'AM' : 'PM';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${suffix}`;
}

/** ISO weekday, 1 = Monday, for a date string, without time-zone surprises. */
export function weekday(iso: string): number {
  const d = new Date(`${iso}T00:00:00Z`).getUTCDay();
  return d === 0 ? 7 : d;
}

/** "Fri Oct 10, 2025". */
export function shortDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  const wd = WEEKDAYS[weekday(iso) - 1]!.slice(0, 3);
  return `${wd} ${MONTHS[d.getUTCMonth()]!.slice(0, 3)} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
}

function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function who(ref: PartyRef, names: Names): string {
  const label = (id: string) => names.parties.find((p) => p.id === id)?.label ?? 'Unknown party';
  if (typeof ref === 'string') return label(ref);
  if (ref.even === ref.odd) return label(ref.even);
  return `${label(ref.even)} in even years, ${label(ref.odd)} in odd years`;
}

function hasChild(ref: PartyRef, names: Names): string {
  return typeof ref === 'string' || ref.even === ref.odd ? `${who(ref, names)} has ${names.child}` : who(ref, names);
}

function every(cycleDays: number, unit: 'week' | 'weekend'): string {
  if (cycleDays === 7) return `every ${unit}`;
  if (cycleDays === 14) return `every other ${unit}`;
  if (cycleDays % 7 === 0) return `every ${cycleDays / 7} ${unit}s`;
  return `every ${cycleDays} days`;
}

function point(p: FixedPoint): string {
  return `${MONTHS[p.month - 1]} ${p.day} at ${clock(p.time)}`;
}

export function describeRule(body: RuleBody, names: Names): string {
  switch (body.type) {
    case 'rotation': {
      const anchorDay = weekday(body.anchor);
      const segs = body.segments.map((s) => {
        const start = WEEKDAYS[(anchorDay - 1 + s.startDay + 700) % 7];
        const end = WEEKDAYS[(anchorDay - 1 + s.endDay + 700) % 7];
        return { s, text: `${start} ${clock(s.startTime)} to ${end} ${clock(s.endTime)}` };
      });
      const first = segs[0]!;
      const startWd = (anchorDay - 1 + first.s.startDay + 700) % 7;
      const span = first.s.endDay - first.s.startDay;
      const weekend = segs.length === 1 && (startWd === 4 || startWd === 5) && span >= 1 && span <= 3;
      const starting = `starting ${shortDate(addDays(body.anchor, first.s.startDay))}`;
      if (segs.length === 1) {
        return `${hasChild(first.s.party, names)} ${every(body.cycleDays, weekend ? 'weekend' : 'week')}, ${first.text}, ${starting}.`;
      }
      const parts = segs.map(({ s, text }) => `${text} (${who(s.party, names)})`).join('; ');
      return `Repeats ${every(body.cycleDays, 'week')}: ${parts}, ${starting}.`;
    }
    case 'midweek': {
      const day = WEEKDAYS[weekday(body.anchor) - 1]!;
      const often = body.everyWeeks === 1 ? `every ${day}` : body.everyWeeks === 2 ? `every other ${day}` : `every ${body.everyWeeks} weeks on ${day}`;
      return `${hasChild(body.party, names)} ${often}, ${clock(body.startTime)} to ${clock(body.endTime)}, starting ${shortDate(body.anchor)}.`;
    }
    case 'block': {
      const [sd, st] = body.start.split('T') as [string, string];
      const [ed, et] = body.end.split('T') as [string, string];
      const when =
        st === '00:00' && et === '00:00'
          ? `from ${shortDate(sd)} through ${shortDate(addDays(ed, -1))}`
          : `from ${shortDate(sd)} at ${clock(st)} to ${shortDate(ed)} at ${clock(et)}`;
      return `${body.label}: ${hasChild(body.party, names)} ${when}.`;
    }
    case 'schoolBreak': {
      const from = body.from === 'release' ? `${clock(body.releaseTime)} on the last school day` : point(body.from);
      const to = body.to === 'return' ? `${clock(body.returnTime)} on the first day back` : point(body.to);
      const alternates = typeof body.party !== 'string' && body.party.even !== body.party.odd;
      const year = alternates ? ` Even or odd follows the year the break ${body.parityYear === 'end' ? 'ends' : 'starts'}.` : '';
      return `${breakName(body.breakKind)}: ${who(body.party, names)}, from ${from} until ${to}.${year}`;
    }
    case 'specialDay': {
      const day =
        body.day === 'mothersDay' ? 'Mother’s Day' : body.day === 'fathersDay' ? 'Father’s Day' : `${MONTHS[body.day.month - 1]} ${body.day.day}`;
      return `${day}: ${hasChild(body.party, names)} from ${clock(body.startTime)} to ${clock(body.endTime)}${
        typeof body.party === 'string' ? ' every year' : ''
      }.`;
    }
    case 'holiday': {
      const offset = (n: number) => (n === 0 ? 'the day' : n === -1 ? 'the day before' : n === 1 ? 'the day after' : n < 0 ? `${-n} days before` : `${n} days after`);
      const weekendNote = body.includeAdjacentWeekend
        ? ' On a Friday it includes the weekend after; on a Monday, the weekend before.'
        : '';
      return `${holidayName(body.holiday)}: ${who(body.party, names)}, from ${clock(body.startTime)} ${offset(body.startOffsetDays)} to ${clock(body.endTime)} ${offset(body.endOffsetDays)}.${weekendNote}`;
    }
  }
}
